import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Users, Shield, MessageCircle, Home, BookOpen, ArrowLeft } from 'lucide-react';
import { PageShell } from '../components/layout/PageShell';

const LoveYourWayEducation = () => {
  const navigate = useNavigate();

  return (
    <PageShell>
      {/* Header */}
      <div className="surface-raised shadow-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center space-x-2 text-slate-600 dark:text-slate-300 hover:text-rose-600 dark:hover:text-rose-400 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back</span>
            </button>
            <div className="flex items-center space-x-2">
              <Heart className="w-6 h-6 text-rose-600 fill-current" />
              <span className="font-playfair text-xl font-bold text-slate-900 dark:text-slate-50">Unity Love</span>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center space-x-2 text-slate-600 dark:text-slate-300 hover:text-rose-600 dark:hover:text-rose-400 transition-colors"
            >
              <Home className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Hero Section */}
        <div className="glass rounded-3xl p-8 lg:p-12 mb-8 text-center slide-up">
          <div className="w-20 h-20 bg-gradient-primary rounded-full flex items-center justify-center mx-auto mb-6">
            <BookOpen className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-playfair text-4xl lg:text-5xl font-bold text-slate-900 dark:text-slate-50 mb-4">
            Love Your Way
          </h1>
          <p className="text-xl text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
            A clear, judgment-free guide to non-monogamous relationships: what they are, 
            how they work, and how to navigate them with honesty and care.
          </p>
        </div>

        {/* Quick Definitions */}
        <div className="glass rounded-2xl p-8 mb-8">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 dark:text-slate-50 mb-6">
            1️⃣ Quick Definitions
          </h2>
          
          <div className="space-y-6">
            <div className="bg-white/50 dark:bg-slate-800/50 rounded-xl p-6">
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">Monogamy</h3>
              <p className="text-slate-700 dark:text-slate-300">
                One romantic/sexual partner at a time, usually exclusive and long-term.
              </p>
            </div>

            <div className="bg-white/50 dark:bg-slate-800/50 rounded-xl p-6">
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">Polyamory</h3>
              <p className="text-slate-700 dark:text-slate-300">
                The ability to love and date more than one person at a time, with everyone's 
                knowledge and consent. It's about <strong>multiple relationships</strong>, 
                not necessarily marriage.
              </p>
            </div>

            <div className="bg-white/50 dark:bg-slate-800/50 rounded-xl p-6">
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">Polygamy</h3>
              <p className="text-slate-700 dark:text-slate-300">
                One person having multiple spouses or life partners, often in a shared family 
                structure. It's about <strong>multi-partner family/marriage</strong>, usually 
                under one relational "container."
              </p>
            </div>

            <div className="bg-rose-50 dark:bg-rose-900/20 border-l-4 border-rose-600 p-4 rounded">
              <p className="text-slate-800 dark:text-slate-200 font-medium">
                Both polyamory and polygamy fall under the big umbrella of consensual 
                non-monogamy, but they're not the same thing.
              </p>
            </div>
          </div>
        </div>

        {/* Shared Core Values */}
        <div className="glass rounded-2xl p-8 mb-8">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 dark:text-slate-50 mb-6">
            2️⃣ Shared Core Values
          </h2>
          <p className="text-slate-700 dark:text-slate-300 mb-6">
            Healthy polyamory and healthy polygamy both run on:
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            {[
              { title: "Honesty", desc: "People tell the truth about connections, feelings, and commitments" },
              { title: "Consent", desc: "Everyone involved knows what's happening and agrees voluntarily" },
              { title: "Communication", desc: "Feelings, jealousy, fears, and needs get discussed" },
              { title: "Boundaries", desc: "Clear agreements about sex, time, intimacy, and home life" },
            ].map((v) => (
              <div key={v.title} className="bg-white/50 dark:bg-slate-800/50 rounded-xl p-4 flex items-start space-x-3">
                <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-green-600 dark:text-green-400 font-bold">✓</span>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-1">{v.title}</h4>
                  <p className="text-sm text-slate-600 dark:text-slate-300">{v.desc}</p>
                </div>
              </div>
            ))}

            <div className="bg-white/50 dark:bg-slate-800/50 rounded-xl p-4 flex items-start space-x-3 md:col-span-2">
              <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-green-600 dark:text-green-400 font-bold">✓</span>
              </div>
              <div>
                <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-1">Responsibility</h4>
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  People own their actions and understand how they affect others
                </p>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-600 p-4 rounded mt-6">
            <p className="text-slate-800 dark:text-slate-200 font-medium">
              If those values are missing, it's not "poly" or "polygamy done right" — 
              it's just dysfunction with a label slapped on.
            </p>
          </div>
        </div>

        {/* Key Differences */}
        <div className="glass rounded-2xl p-8 mb-8">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 dark:text-slate-50 mb-6">
            3️⃣ Key Differences
          </h2>
          
          <div className="overflow-x-auto">
            <table className="w-full bg-white/50 dark:bg-slate-800/50 rounded-xl">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700">
                  <th className="text-left p-4 font-semibold text-slate-900 dark:text-slate-100">Aspect</th>
                  <th className="text-left p-4 font-semibold text-rose-600 dark:text-rose-400">Polyamory</th>
                  <th className="text-left p-4 font-semibold text-purple-600 dark:text-purple-400">Polygamy</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-slate-100 dark:border-slate-700/50">
                  <td className="p-4 font-medium text-slate-700 dark:text-slate-300">Structure</td>
                  <td className="p-4 text-slate-600 dark:text-slate-400">Multiple romantic/sexual relationships</td>
                  <td className="p-4 text-slate-600 dark:text-slate-400">Multiple spouses / long-term partners</td>
                </tr>
                <tr className="border-b border-slate-100 dark:border-slate-700/50">
                  <td className="p-4 font-medium text-slate-700 dark:text-slate-300">Living</td>
                  <td className="p-4 text-slate-600 dark:text-slate-400">May or may not involve cohabitation</td>
                  <td className="p-4 text-slate-600 dark:text-slate-400">Usually family/household focused</td>
                </tr>
                <tr className="border-b border-slate-100 dark:border-slate-700/50">
                  <td className="p-4 font-medium text-slate-700 dark:text-slate-300">Network</td>
                  <td className="p-4 text-slate-600 dark:text-slate-400">Often more fluid and network-based</td>
                  <td className="p-4 text-slate-600 dark:text-slate-400">More structured, "one family unit"</td>
                </tr>
                <tr>
                  <td className="p-4 font-medium text-slate-700 dark:text-slate-300">Focus</td>
                  <td className="p-4 text-slate-600 dark:text-slate-400">Less about "family structure," more about consent + connection</td>
                  <td className="p-4 text-slate-600 dark:text-slate-400">Includes parenting, shared finances, legacy</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-600 p-4 rounded mt-6">
            <p className="text-slate-800 dark:text-slate-200 mb-2">
              <strong>Some Unity Love users will be polyamorous but not into polygamy.</strong>
            </p>
            <p className="text-slate-800 dark:text-slate-200 mb-2">
              <strong>Some will be polygamous but not into wide poly networks.</strong>
            </p>
            <p className="text-slate-800 dark:text-slate-200">
              <strong>Some are cool with both structures.</strong> Your profile + filters make those differences clear.
            </p>
          </div>
        </div>

        {/* Why People Choose */}
        <div className="glass rounded-2xl p-8 mb-8">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 dark:text-slate-50 mb-4">
            4️⃣ Why People Choose Non-Monogamy
          </h2>
          
          <blockquote className="border-l-4 border-rose-600 pl-4 mb-6 italic text-slate-700 dark:text-slate-300">
            <p className="mb-2">The goal isn't "poly is better" or "mono is better."</p>
            <p className="font-semibold">
              The question is: What structure makes you more honest, more loving, 
              and more at peace with yourself?
            </p>
          </blockquote>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white/50 dark:bg-slate-800/50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3">Monogamy</h3>
              <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-300">
                <li>• Deep focus on one person</li>
                <li>• Feel safest with exclusivity</li>
                <li>• Fits their faith, culture, or nervous system</li>
              </ul>
            </div>

            <div className="bg-white/50 dark:bg-slate-800/50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-rose-600 dark:text-rose-400 mb-3">Polyamory</h3>
              <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-300">
                <li>• Can love more than one partner deeply</li>
                <li>• Want fewer secrets, more honesty</li>
                <li>• Enjoy emotional variety and network support</li>
              </ul>
            </div>

            <div className="bg-white/50 dark:bg-slate-800/50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-purple-600 dark:text-purple-400 mb-3">Polygamy</h3>
              <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-300">
                <li>• Want stable multi-partner family</li>
                <li>• Value group economics and shared responsibility</li>
                <li>• Matches spiritual and cultural beliefs</li>
              </ul>
            </div>
          </div>

          <div className="bg-rose-50 dark:bg-rose-900/20 border-l-4 border-rose-600 p-4 rounded mt-6">
            <p className="text-slate-800 dark:text-slate-200">
              <strong>Unity Love doesn't rank these.</strong> It simply creates space 
              for honest connection around them.
            </p>
          </div>
        </div>

        {/* Myths vs Reality */}
        <div className="glass rounded-2xl p-8 mb-8">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 dark:text-slate-50 mb-6">
            5️⃣ Reality vs Public Opinion
          </h2>

          <div className="space-y-6">
            {[
              {
                myth: '"Non-monogamy is just an excuse to cheat."',
                points: ['<strong>Cheating</strong> = lies + broken agreements', '<strong>Polyamory & polygamy</strong> = agreements made up front'],
                closing: "Cheating happens when people hide the truth. Non-monogamy at its best happens when people commit to the truth.",
              },
              {
                myth: '"People in poly or polygamous relationships don\'t really commit."',
                points: ['Long-term commitment', 'Co-parenting', 'Shared housing', 'Shared responsibility', 'Deep emotional bonds'],
                closing: "Commitment is about how you show up, not how many people you love.",
                pointPrefix: '✅ ',
                intro: 'A lot of poly and polygamous relationships involve:',
              },
              {
                myth: '"Non-monogamy always leads to jealousy and drama."',
                points: ['<strong>In toxic setups:</strong> jealousy turns into control, spying, and games', '<strong>In healthy setups:</strong> jealousy becomes a signal for conversation, reassurance, and inner work'],
                closing: "Poly and polygamous folks aren't immune to jealousy — they just agree to face it instead of pretending it isn't there.",
                intro: 'Jealousy exists in every structure. The difference is:',
              },
            ].map((item, i) => (
              <div key={i} className="bg-white/50 dark:bg-slate-800/50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-2">
                  ❌ Myth: {item.myth}
                </h3>
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 mt-3">
                  <p className="font-semibold text-green-800 dark:text-green-300 mb-2">✅ Reality:</p>
                  {item.intro && <p className="text-slate-700 dark:text-slate-300 text-sm mb-2">{item.intro}</p>}
                  <ul className="space-y-1 text-slate-700 dark:text-slate-300 text-sm">
                    {item.points.map((p, j) => (
                      <li key={j} dangerouslySetInnerHTML={{ __html: `• ${item.pointPrefix || ''}${p}` }} />
                    ))}
                  </ul>
                  <p className="mt-3 text-slate-700 dark:text-slate-300 text-sm font-semibold">
                    {item.closing}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* How Unity Love Helps */}
        <div className="glass rounded-2xl p-8 mb-8">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 dark:text-slate-50 mb-6">
            7️⃣ How Unity Love Helps You Navigate All This
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: Users, color: 'rose', title: 'Profile Fields', desc: 'Name your relationship style: Monogamous, Polyamorous, Polygamous, or other non-monogamous structures.' },
              { icon: MessageCircle, color: 'purple', title: "Say What You're Open To", desc: 'Polygamous families, triads, quads, closed or open group relationships, monogamous partners only, or a mix.' },
              { icon: Shield, color: 'blue', title: 'Find Your People', desc: 'Filter by: Only polyamory, only polygamy, only monogamous, or a blend based on your openness.' },
              { icon: Shield, color: 'green', title: 'Privacy Controls', desc: 'Choose: Public (everyone sees), Matches only (hidden until match), or Hidden (you decide when to share).' },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="bg-white/50 dark:bg-slate-800/50 rounded-xl p-6">
                  <div className={`w-12 h-12 bg-${item.color}-100 dark:bg-${item.color}-900/30 rounded-full flex items-center justify-center mb-4`}>
                    <Icon className={`w-6 h-6 text-${item.color}-600 dark:text-${item.color}-400`} />
                  </div>
                  <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-600 dark:text-slate-300">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Our Promise */}
        <div className="glass rounded-2xl p-8 text-center">
          <Heart className="w-16 h-16 text-rose-600 fill-current mx-auto mb-4" />
          <h2 className="font-playfair text-3xl font-bold text-slate-900 dark:text-slate-50 mb-4">
            Our Promise
          </h2>
          <p className="text-xl text-slate-700 dark:text-slate-300 max-w-2xl mx-auto">
            Unity Love is not here to pressure you into any structure.
          </p>
          <p className="text-xl text-slate-700 dark:text-slate-300 font-semibold mt-2">
            It's here so you don't have to hide the one that's already in your heart.
          </p>
        </div>

        {/* Related Resources */}
        <div className="mt-8 grid md:grid-cols-3 gap-4">
          <button
            onClick={() => navigate('/guidelines')}
            className="glass rounded-xl p-6 text-center hover:shadow-lg transition-all"
          >
            <BookOpen className="w-8 h-8 text-rose-600 mx-auto mb-2" />
            <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-1">Community Guidelines</h3>
            <p className="text-sm text-slate-600 dark:text-slate-300">Our ethics and values</p>
          </button>

          <button
            onClick={() => navigate('/safety')}
            className="glass rounded-xl p-6 text-center hover:shadow-lg transition-all"
          >
            <Shield className="w-8 h-8 text-rose-600 mx-auto mb-2" />
            <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-1">Privacy & Safety</h3>
            <p className="text-sm text-slate-600 dark:text-slate-300">Your security matters</p>
          </button>

          <button
            onClick={() => navigate('/help')}
            className="glass rounded-xl p-6 text-center hover:shadow-lg transition-all"
          >
            <MessageCircle className="w-8 h-8 text-rose-600 mx-auto mb-2" />
            <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-1">Help Center</h3>
            <p className="text-sm text-slate-600 dark:text-slate-300">Get support</p>
          </button>
        </div>
      </div>
    </PageShell>
  );
};

export default LoveYourWayEducation;
